import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { company, presence } from '../content/company';
import type { Product } from '../content/products';
import { AppIcon } from './Brand';

export function SectionHead({
  eyebrow,
  title,
  lede,
  center = false,
}: {
  eyebrow: string;
  title: ReactNode;
  lede?: ReactNode;
  center?: boolean;
}) {
  return (
    <div className={`section-head${center ? ' section-head--center' : ''}`}>
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="section-head__title">{title}</h2>
      {lede && <p className="section-head__lede">{lede}</p>}
    </div>
  );
}

export function PageHero({ eyebrow, title, lede }: { eyebrow: string; title: ReactNode; lede: ReactNode }) {
  return (
    <section className="page-hero">
      <div className="container">
        <p className="eyebrow eyebrow--light">{eyebrow}</p>
        <h1 className="page-hero__title">{title}</h1>
        <p className="page-hero__lede">{lede}</p>
      </div>
    </section>
  );
}

export function ProductCard({ product }: { product: Product }) {
  return (
    <article className="product-card" style={{ ['--accent' as string]: product.accent }}>
      <div className="product-card__top">
        <AppIcon id={product.id} size={60} />
        <span className="status">{product.status}</span>
      </div>
      <p className="product-card__cat">{product.category}</p>
      <h3 className="product-card__name">{product.name}</h3>
      <p className="product-card__tagline">{product.tagline}</p>
      <p className="product-card__by">A {company.shortName} product</p>
      <Link className="product-card__link" to={`/products#${product.id}`}>
        Learn about {product.name} <span aria-hidden="true">→</span>
      </Link>
    </article>
  );
}

/**
 * The company's legal identity as a definition list. It appears on the
 * Company page and on the home page, because it is the block a verifier looks
 * for: name, number, date, jurisdiction and registered address.
 */
export function CompanyFacts() {
  const rows: [string, ReactNode][] = [
    ['Legal name', company.legalName],
    ['Company type', `${company.entityType}`],
    ['Company number', company.registrationNumber],
    ['Incorporated', `${company.incorporated}, under the ${company.incorporationAct}`],
    ['Jurisdiction', company.jurisdiction],
    ['Autonomous system', company.asn],
    [
      'Registered address',
      <>
        {company.address.lines.map((l) => (
          <span key={l} className="block">
            {l}
          </span>
        ))}
        <span className="block">{company.address.country}</span>
      </>,
    ],
    ['Email', <a href={`mailto:${company.email}`}>{company.email}</a>],
    ['Telephone', <a href={company.phoneHref}>{company.phoneDisplay}</a>],
    ['Website', <a href={company.url}>{company.url.replace('https://', '')}</a>],
  ];
  return (
    <dl className="facts">
      {rows.map(([k, v]) => (
        <div className="facts__row" key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Presence() {
  return (
    <div className="presence">
      {presence.map((p) => (
        <article key={p.region} className="presence__card">
          <p className="presence__region">{p.region}</p>
          <h3 className="presence__places">{p.places.join(' · ')}</h3>
          <p className="presence__note">{p.note}</p>
        </article>
      ))}
    </div>
  );
}

export function CtaBand() {
  return (
    <section className="cta-band">
      <div className="container cta-band__inner">
        <div>
          <h2 className="cta-band__title">Partner, invest or build with us.</h2>
          <p className="cta-band__lede">
            We work with manufacturers, distributors, network operators and investors who want trustworthy digital
            products in the hands of more people.
          </p>
        </div>
        <div className="cta-band__actions">
          <a className="btn btn--primary" href={`mailto:${company.email}?subject=Partnership%20enquiry`}>
            Start a conversation
          </a>
          <Link className="btn btn--ghost-light" to="/contact">
            Contact details
          </Link>
        </div>
      </div>
    </section>
  );
}
